import { Building2, MessageCircle, Send, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  advertiserTypeOptions, apartmentTypeOptions, buildingAgeOptions, currencyOptions, formatApartmentType,
  formatBuildingAge, furnishedOptions, formatFurnished, propertyTypeOptions, formatPropertyType,
  type AdvertiserType, type ApartmentType, type BuildingAge, type CurrencyCode, type FurnishedState,
  type ListingType, type PropertyType,
} from "@/lib/listings";
import { toast } from "@/hooks/use-toast";
import { submitPropertySchema, normalizeWhatsapp } from "@/lib/validation";
import { normalizeDigits } from "@/lib/digits";
import { validateImageFile, compressImages } from "@/lib/imageOptimize";
import { AreaPicker, resolveArea } from "@/components/AreaPicker";
import { OTHER_AREA_VALUE } from "@/data/istanbulAreas";
import { PhoneInput } from "@/components/PhoneInput";
import { useSetting } from "@/lib/siteSettings";
import { SEO } from "@/lib/seo";

const SubmitProperty = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const autoApprove = useSetting("auto_approve_submissions", "true") === "true";
  const whatsappNumber = useSetting("whatsapp_number", "00905545684231");
  const waDigits = whatsappNumber.replace(/\D/g, "").replace(/^0+/, "");
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [savedHint, setSavedHint] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [form, setForm] = useState({
    listing_type: "rent" as ListingType,
    property_type: "apartment" as PropertyType,
    apartment_type: "2+1" as ApartmentType,
    apartment_type_note: "",
    rooms_count: "3", // for villa-like types only (maps to apartment_type)
    building_age: "new" as BuildingAge,
    title: "",
    description: "",
    area_value: "",
    area_other: "",
    floor_number: "",
    building_type: "" as "" | "standalone" | "complex",
    has_gym: false,
    bathrooms: "",
    kitchen_type: "" as "" | "open" | "closed",
    balcony: false,
    size_sqm: "",
    furnished: "no" as FurnishedState,
    parking: false,
    price: "",
    currency: "USD" as CurrencyCode,
    deposit_required: false,
    deposit_amount: "",
    deposit_currency: "USD" as CurrencyCode,
    deposit_note: "",
    google_maps_url: "",
    extra_notes: "",
    advertiser_name: "",
    advertiser_type: "owner" as AdvertiserType,
    advertiser_whatsapp: "",
    advertiser_email: "",
  });

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((s) => ({ ...s, [k]: v }));

  // Floor number is meaningless for ground-based property types (villa / townhouse / other).
  const hideFloor = form.property_type === "villa" || form.property_type === "other";


  const DRAFT_KEY = "submit_property_draft_v1";
  // Restore draft once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { savedAt: number; form: typeof form };
      if (parsed?.savedAt && Date.now() - parsed.savedAt < 24 * 60 * 60 * 1000 && parsed.form) {
        setForm((s) => ({ ...s, ...parsed.form }));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Debounced autosave
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: Date.now(), form }));
        setSavedHint(true);
        setTimeout(() => setSavedHint(false), 1500);
      } catch { /* ignore quota */ }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [form]);

  // Reset deposit when switching to sale
  useEffect(() => {
    if (form.listing_type === "sale" && (form.deposit_required || form.deposit_amount || form.deposit_note)) {
      setForm((s) => ({ ...s, deposit_required: false, deposit_amount: "", deposit_note: "" }));
    }
  }, [form.listing_type]);

  const buildFallbackWaUrl = () => {
    const area = resolveArea(form.area_value, form.area_other);
    const lines = [
      "السلام عليكم، حاولت أنشر عقار على ISTANOVA لكن واجهت مشكلة.",
      "",
      "البيانات:",
      `الاسم: ${form.advertiser_name || "-"}`,
      `واتساب: ${form.advertiser_whatsapp || "-"}`,
      `المنطقة: ${area || "-"}`,
      `النوع: ${form.property_type} / ${form.apartment_type}`,
      `السعر: ${form.price || "-"} ${form.currency}`,
      `العنوان: ${form.title || "-"}`,
    ];
    return `https://wa.me/${waDigits}?text=${encodeURIComponent(lines.join("\n"))}`;
  };
  const cornerWaUrl = `https://wa.me/${waDigits}?text=${encodeURIComponent("السلام عليكم، أبي أنشر عقار على ISTANOVA")}`;

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []).slice(0, 15 - files.length);
    const valid: File[] = [];
    for (const f of list) {
      const err = validateImageFile(f);
      if (err) {
        toast({ title: err, description: f.name, variant: "destructive" });
        continue;
      }
      valid.push(f);
    }
    setFiles((s) => [...s, ...valid]);
  };
  const removeFile = (i: number) => setFiles((s) => s.filter((_, idx) => idx !== i));

  const logToServer = async (kind: "error_log" | "debug_log", message: string, context: Record<string, unknown>) => {
    try {
      await supabase.rpc("log_client_error" as never, { _message: message, _context: context as never, _kind: kind } as never);
    } catch { /* swallow — logging must never throw */ }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ───── Phase 1: validateForm ─────
    // Images are OPTIONAL (0–15). Only enforce upper bound.
    if (files.length > 15) {
      toast({ title: t("validate.maxImages"), variant: "destructive" });
      return;
    }
    const area = resolveArea(form.area_value, form.area_other);
    if (form.apartment_type === "other" && !form.apartment_type_note.trim()) {
      toast({ title: t("field.apartmentType.specifyOther"), variant: "destructive" });
      return;
    }
    const mapsUrl = form.google_maps_url.trim();
    if (mapsUrl && !(/^https:\/\//i.test(mapsUrl) && /(google|goo\.gl|maps)/i.test(mapsUrl))) {
      toast({ title: t("submit.googleMapsUrl.invalid"), variant: "destructive" });
      return;
    }
    const parsed = submitPropertySchema().safeParse({
      title: form.title, description: form.description, area,
      apartment_type_note: form.apartment_type_note, bathrooms: form.bathrooms,
      size_sqm: form.size_sqm, price: form.price, google_maps_url: form.google_maps_url,
      advertiser_name: form.advertiser_name, advertiser_whatsapp: form.advertiser_whatsapp,
      advertiser_email: form.advertiser_email,
    });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast({ title: first.message, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    setFatalError(null);
    setUploadProgress(null);
    let phase = "init";
    let createdListingId: string | null = null;
    const startedAt = Date.now();

    try {
      // ───── Phase 2: compressImages (per-file try/catch) ─────
      phase = "compress_images";
      const compressed: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        try {
          const [single] = await compressImages([f]);
          compressed.push(single);
        } catch (err) {
          await logToServer("error_log", "Image compression failed", {
            phase: `compress_image_${i}`,
            fileName: f.name, fileSize: f.size, fileType: f.type,
            errorType: classifyError(err),
            errorMessage: extractErrorMessage(err),
          });
          throw new PhasedError("compress_images", "submit.errors.compress", err, { fileName: f.name, index: i });
        }
      }

      // ───── Phase 3: insertListing ─────
      phase = "insert_listing";
      // CRITICAL: set created_by from the current session so the RLS WITH CHECK
      // (`(auth.uid() IS NULL AND created_by IS NULL) OR (auth.uid() = created_by)`)
      // passes for both anonymous AND logged-in submitters. Forgetting this
      // re-introduces the recurring "تعذّر قبول هذا العرض" RLS bug.
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUserId = sessionData.session?.user.id ?? null;
      // Generate id client-side so we don't need INSERT...RETURNING (which would
      // require SELECT permission on the freshly inserted pending row — denied by
      // the blind-broker SELECT policy for non-admins).
      const newListingId = crypto.randomUUID();
      const { error: lErr } = await supabase.from("listings").insert({
        id: newListingId,
        title: form.title.trim(),
        description: form.description.trim(),
        area,
        listing_type: form.listing_type,
        property_type: form.property_type,
        apartment_type: form.apartment_type,
        apartment_type_note: form.apartment_type === "other" ? form.apartment_type_note.trim() : null,
        building_age: form.building_age,
        bathrooms: Number(normalizeDigits(form.bathrooms)),
        size_sqm: Number(normalizeDigits(form.size_sqm)),
        floor_number: hideFloor ? null : (form.floor_number || null),
        building_type: form.building_type || null,
        has_gym: form.has_gym,
        kitchen_type: form.kitchen_type || null,
        balcony: form.balcony,
        parking: form.parking,
        furnished: form.furnished,
        price: Number(normalizeDigits(form.price)),
        currency: form.currency,
        eligible_for_citizenship: false,
        deposit_required: form.deposit_required,
        deposit_amount: form.deposit_required && form.deposit_amount ? Number(normalizeDigits(form.deposit_amount)) : null,
        deposit_currency: form.deposit_required ? form.deposit_currency : null,
        deposit_note: form.deposit_required ? form.deposit_note || null : null,
        google_maps_url: form.google_maps_url.trim() || null,
        extra_notes: form.extra_notes || null,
        is_published: true,
        approval_status: "pending",
        created_by: sessionUserId,
      });

      if (lErr) {
        await logToServer("error_log", "Listing insert failed", {
          phase: "insert_listing",
          errorType: classifyError(lErr),
          pgCode: (lErr as { code?: string } | null)?.code ?? null,
          errorMessage: extractErrorMessage(lErr),
          autoApproveFlag: autoApprove,
        });
        throw new PhasedError("insert_listing", "submit.errors.listingInsert", lErr);
      }
      const listing = { id: newListingId };
      createdListingId = listing.id;


      // ───── Phase 4 + 5: uploadImages + insertListingImages (SKIPPED if no files) ─────
      if (compressed.length > 0) {
        phase = "upload_images";
        const imageRows: { listing_id: string; image_url: string; storage_path: string; display_order: number }[] = [];
        setUploadProgress({ done: 0, total: compressed.length, failed: 0 });
        let failedCount = 0;
        let firstUploadErr: unknown = null;
        for (let i = 0; i < compressed.length; i++) {
          const file = compressed[i];
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const path = `${listing.id}/${Date.now()}_${i}_${safeName}`;
          let uploaded = false;
          let lastErr: unknown = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            const { error: uErr } = await supabase.storage.from("listing-images").upload(path, file, {
              upsert: false,
              contentType: file.type || "image/jpeg",
            });
            if (!uErr) { uploaded = true; break; }
            lastErr = uErr;
            console.error("[storage upload error]", { attempt, path, error: uErr });
            if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt)));
          }
          if (uploaded) {
            const { data: pub } = supabase.storage.from("listing-images").getPublicUrl(path);
            imageRows.push({ listing_id: listing.id, image_url: pub.publicUrl, storage_path: path, display_order: imageRows.length });
          } else {
            failedCount++;
            if (!firstUploadErr) firstUploadErr = lastErr;
            await logToServer("error_log", "Image upload failed after retries", {
              phase: `upload_image_${i}`,
              fileName: file.name, fileSize: file.size, fileType: file.type,
              storagePath: path,
              errorType: classifyError(lastErr),
              errorMessage: extractErrorMessage(lastErr),
              listingId: listing.id,
            });
          }
          setUploadProgress({ done: i + 1, total: compressed.length, failed: failedCount });
        }
        if (imageRows.length === 0) {
          const key = friendlyErrorKey(firstUploadErr) || "submit.errors.imageUpload";
          throw new PhasedError("upload_images", key, firstUploadErr ?? new Error("All image uploads failed"));
        }
        if (failedCount > 0) {
          toast({ title: t("submit.partialUpload", { defaultValue: `تم رفع ${imageRows.length} من ${compressed.length} صور` }) });
        }

        // ───── Phase 5: insertListingImages ─────
        phase = "insert_listing_images";
        const { error: iErr } = await supabase.from("listing_images").insert(imageRows);
        if (iErr) {
          await logToServer("error_log", "Listing images insert failed", {
            phase: "insert_listing_images",
            errorType: classifyError(iErr),
            pgCode: (iErr as { code?: string } | null)?.code ?? null,
            errorMessage: extractErrorMessage(iErr),
            listingId: listing.id, imageCount: imageRows.length,
          });
          throw new PhasedError("insert_listing_images", "submit.errors.imageLink", iErr);
        }
      }

      // ───── Phase 6: insertPropertySubmission ─────
      phase = "insert_property_submission";
      const { error: sErr } = await supabase.from("property_submissions").insert({
        listing_id: listing.id,
        advertiser_name: form.advertiser_name.trim(),
        advertiser_type: form.advertiser_type,
        advertiser_whatsapp: normalizeWhatsapp(form.advertiser_whatsapp),
        advertiser_email: form.advertiser_email.trim() || null,
      });
      if (sErr) {
        await logToServer("error_log", "Property submission insert failed", {
          phase: "insert_property_submission",
          errorType: classifyError(sErr),
          pgCode: (sErr as { code?: string } | null)?.code ?? null,
          errorMessage: extractErrorMessage(sErr),
          listingId: listing.id,
        });
        // Do NOT roll back the listing — it's published; tell the user contact info is missing.
        throw new PhasedError("insert_property_submission", "submit.errors.contactInsert", sErr);
      }

      // ───── Success: 48h debug trace ─────
      await logToServer("debug_log", "Property submission succeeded", {
        phase: "success",
        listingId: listing.id,
        approvalStatus: autoApprove ? "approved" : "pending",
        imageCount: compressed.length,
        durationMs: Date.now() - startedAt,
        advertiserType: form.advertiser_type,
      });

      toast({ title: t("submit.successTitle"), description: t(autoApprove ? "submit.successPublished" : "submit.successPending") });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setFatalError(null);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      const isPhasedErr = err instanceof PhasedError;
      const failedPhase = isPhasedErr ? err.phase : phase;
      if (createdListingId && failedPhase === "upload_images") {
        // Use SECURITY DEFINER RPC so anon submitters can roll back their own orphan listing
        // (anon role has no DELETE on listings; RPC narrowly deletes only own pending row).
        await supabase.rpc("rollback_anon_listing", { _listing_id: createdListingId });
        await supabase.from("listings").delete().eq("id", createdListingId); // covers logged-in owners
        await logToServer("debug_log", "Rolled back orphan listing", {
          phase: "rollback_listing",
          listingId: createdListingId,
          reason: failedPhase,
        });
      }

      // Map common errors to actionable Arabic messages
      const rawCause = isPhasedErr ? (err as PhasedError).cause : err;
      const pgCode = (rawCause as { code?: string } | null | undefined)?.code;
      const kind = classifyError(rawCause);
      let friendly: string;
      if (pgCode === "42501" || kind === "RLSViolation") {
        friendly = t("submit.errors.permission", { defaultValue: "خطأ في الصلاحيات، حاول مجدداً أو ابعث على واتساب" });
      } else if (kind === "NetworkError") {
        friendly = t("submit.errors.network", { defaultValue: "تحقق من الاتصال وحاول مجدداً" });
      } else if (kind === "StorageError") {
        friendly = t("submit.errors.imageUpload", { defaultValue: "فشل رفع الصور، حاول بصور أصغر" });
      } else {
        const messageKey = isPhasedErr ? err.messageKey : "submit.errors.generic";
        friendly = t(messageKey, { defaultValue: extractErrorMessage(err) });
      }
      setFatalError(friendly);
      toast({ title: t("submit.errorTitle"), description: friendly, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-background px-4 py-12 sm:px-6 lg:px-8">
      <SEO title={t("seo.submit.title")} description={t("seo.submit.description")} path="/submit" />
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-2 text-sm font-semibold text-primary">
          <Building2 className="h-4 w-4" /> {t("submit.badge")}
        </div>
        <h1 className="text-3xl font-black sm:text-4xl">{t("submit.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("submit.subtitle")}</p>
        <div className="mt-2 h-5 text-xs text-muted-foreground" aria-live="polite">
          {savedHint && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">تم الحفظ تلقائياً</span>}
        </div>
        {!autoApprove && (
          <p className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">{t("submit.reviewNotice")}</p>
        )}

        <form onSubmit={submit} className="mt-8 space-y-8 rounded-2xl border border-border bg-card p-6 shadow-soft">
          {/* SECTION A */}
          <Section title={t("submit.sectionInfo")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("submit.listingType")}>
                <div className="flex gap-2">
                  {(["rent", "sale"] as ListingType[]).map((v) => (
                    <button type="button" key={v} onClick={() => set("listing_type", v)}
                      className={`flex-1 rounded-md border px-3 py-2 text-sm font-bold ${form.listing_type === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>{t(`listingType.${v}`)}</button>
                  ))}
                </div>
              </Field>
              <Field label={t("submit.propertyType")}>
                <select value={form.property_type} onChange={(e) => set("property_type", e.target.value as PropertyType)} className={inputCls}>
                  {propertyTypeOptions.map((o) => <option key={o} value={o}>{formatPropertyType(o)}</option>)}
                </select>
              </Field>
            </div>
            {(["villa", "other"] as PropertyType[]).includes(form.property_type) ? (
              <Field label={t("submit.roomsCount", { defaultValue: "عدد الغرف" })}>
                <select
                  value={form.rooms_count}
                  onChange={(e) => {
                    const v = e.target.value;
                    const map: Record<string, ApartmentType> = { "3": "3+1", "4": "4+1", "5": "5+2", "6": "other", "7+": "other" };
                    setForm((s) => ({ ...s, rooms_count: v, apartment_type: map[v] ?? "other", apartment_type_note: v === "6" || v === "7+" ? `${v} ${t("submit.roomsCount.suffix", { defaultValue: "غرف" })}` : "" }));
                  }}
                  className={inputCls}
                >
                  {["3", "4", "5", "6", "7+"].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
            ) : (
              <Field label={t("submit.apartmentType")}>
                <select value={form.apartment_type} onChange={(e) => set("apartment_type", e.target.value as ApartmentType)} className={inputCls}>
                  {apartmentTypeOptions.filter((o) => o !== "villa").map((o) => <option key={o} value={o}>{formatApartmentType(o)}</option>)}
                </select>
              </Field>
            )}
            <Field label={t("submit.buildingAge.label")}>
              <select value={form.building_age} onChange={(e) => set("building_age", e.target.value as BuildingAge)} required className={inputCls}>
                {buildingAgeOptions.map((o) => <option key={o} value={o}>{formatBuildingAge(o)}</option>)}
              </select>
            </Field>
            <Field label={t("submit.titleField")}><input value={form.title} onChange={(e) => set("title", e.target.value)} required className={inputCls} /></Field>
            {form.apartment_type === "other" && (
              <Field label={t("field.apartmentType.specifyOther")}>
                <input value={form.apartment_type_note} onChange={(e) => set("apartment_type_note", e.target.value)} required className={inputCls} />
              </Field>
            )}
            <Field label={t("submit.description")}>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className={inputCls} />
              <span className="mt-1 block text-xs text-muted-foreground">{t("field.description.optional")}</span>
            </Field>
            <Field label={t("submit.area")}>
              <AreaPicker
                value={form.area_value}
                otherText={form.area_other}
                onChange={(v, other) => setForm((s) => ({ ...s, area_value: v, area_other: other }))}
                required={form.area_value === OTHER_AREA_VALUE}
              />
            </Field>
            <div className={`grid gap-4 ${hideFloor ? "" : "sm:grid-cols-2"}`}>
              {!hideFloor && (
                <Field label={t("submit.floorNumber")}><input value={form.floor_number} onChange={(e) => set("floor_number", e.target.value)} className={inputCls} /></Field>
              )}
              <Field label={t("submit.buildingType")}>
                <select value={form.building_type} onChange={(e) => set("building_type", e.target.value as never)} className={inputCls}>
                  <option value="">{t("submit.selectPlaceholder")}</option>
                  <option value="standalone">{t("building.standalone")}</option>
                  <option value="complex">{t("building.complex")}</option>
                </select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("submit.bathrooms")}><input inputMode="numeric" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} required className={inputCls} /></Field>
              <Field label={t("submit.size")}><input inputMode="numeric" value={form.size_sqm} onChange={(e) => set("size_sqm", e.target.value)} required className={inputCls} /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("submit.kitchen")}>
                <select value={form.kitchen_type} onChange={(e) => set("kitchen_type", e.target.value as never)} className={inputCls}>
                  <option value="">{t("submit.selectPlaceholder")}</option>
                  <option value="open">{t("kitchen.open")}</option>
                  <option value="closed">{t("kitchen.closed")}</option>
                </select>
              </Field>
              <Field label={t("submit.furnished")}>
                <select value={form.furnished} onChange={(e) => set("furnished", e.target.value as FurnishedState)} className={inputCls}>
                  {furnishedOptions.map((f) => <option key={f} value={f}>{formatFurnished(f)}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <Toggle label={t("details.specBalcony")} value={form.balcony} onChange={(v) => set("balcony", v)} />
              <Toggle label={t("details.specParking")} value={form.parking} onChange={(v) => set("parking", v)} />
              <Toggle label={t("submit.gymSports")} value={form.has_gym} onChange={(v) => set("has_gym", v)} />
            </div>
          </Section>

          {/* SECTION B */}
          <Section title={t("submit.sectionPricing")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("submit.price")}><input inputMode="numeric" value={form.price} onChange={(e) => set("price", e.target.value)} required className={inputCls} /></Field>
              <Field label={t("submit.currency")}>
                <select value={form.currency} onChange={(e) => set("currency", e.target.value as CurrencyCode)} className={inputCls}>
                  {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            {/* Citizenship eligibility is set by admin, not the submitter */}
            {form.listing_type === "rent" && (
              <>
                <Toggle label={t("submit.depositRequired")} value={form.deposit_required} onChange={(v) => set("deposit_required", v)} />
                {form.deposit_required && (
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label={t("submit.depositAmount")}><input inputMode="numeric" value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} className={inputCls} /></Field>
                    <Field label={t("submit.depositCurrency")}>
                      <select value={form.deposit_currency} onChange={(e) => set("deposit_currency", e.target.value as CurrencyCode)} className={inputCls}>
                        {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label={t("submit.depositNote")}><input value={form.deposit_note} onChange={(e) => set("deposit_note", e.target.value)} placeholder={t("submit.depositNotePlaceholder")} className={inputCls} /></Field>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* SECTION C */}
          <Section title={t("submit.googleMapsUrl.label")}>
            <Field label={t("submit.googleMapsUrl.label")}>
              <input
                value={form.google_maps_url}
                onChange={(e) => set("google_maps_url", e.target.value)}
                placeholder={t("submit.googleMapsUrl.placeholder")}
                className={inputCls}
              />
              <span className="mt-1 block text-xs text-muted-foreground">{t("submit.googleMapsUrl.help")}</span>
            </Field>
          </Section>

          {/* SECTION D */}
          <Section title={t("submit.sectionImages")}>
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-background p-8 text-center transition hover:border-primary">
              <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-bold">{t("submit.uploadClick")}</span>
              <span className="mt-1 text-xs text-muted-foreground">{t("submit.uploadCount", { n: files.length })}</span>
              <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" disabled={files.length >= 15} />
            </label>
            {files.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg border border-border">
                    <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removeFile(i)} className="absolute end-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-destructive text-destructive-foreground"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            {uploadProgress && uploadProgress.total > 0 && (
              <div className="rounded-lg border border-border bg-background p-3">
                <div className="mb-2 flex items-center justify-between text-xs font-bold">
                  <span>
                    {uploadProgress.done < uploadProgress.total
                      ? `جاري رفع الصورة ${uploadProgress.done + 1} من ${uploadProgress.total}...`
                      : `تم رفع ${uploadProgress.done - uploadProgress.failed} من ${uploadProgress.total} صور`}
                  </span>
                  {uploadProgress.failed > 0 && (
                    <span className="text-destructive">فشل {uploadProgress.failed}</span>
                  )}
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </Section>

          {/* SECTION E */}
          <Section title={t("submit.sectionExtra")}>
            <textarea value={form.extra_notes} onChange={(e) => set("extra_notes", e.target.value)} rows={3} className={inputCls} />
          </Section>

          {/* SECTION F */}
          <Section title={t("submit.sectionContact")}>
            <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              {t("submit.contactNote")}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("submit.yourName")}><input value={form.advertiser_name} onChange={(e) => set("advertiser_name", e.target.value)} required className={inputCls} /></Field>
              <Field label={t("submit.advertiserType")}>
                <select value={form.advertiser_type} onChange={(e) => set("advertiser_type", e.target.value as AdvertiserType)} className={inputCls}>
                  {advertiserTypeOptions.map((opt) => <option key={opt} value={opt}>{t(`advertiser.${opt}`)}</option>)}
                </select>
              </Field>
              <Field label={t("submit.advertiserWhatsapp")}><PhoneInput value={form.advertiser_whatsapp} onChange={(v) => set("advertiser_whatsapp", v)} required /></Field>
              <Field label={t("submit.advertiserEmail")}><input type="email" value={form.advertiser_email} onChange={(e) => set("advertiser_email", e.target.value)} className={inputCls} /></Field>
            </div>
          </Section>

          {fatalError && (
            <div className="rounded-2xl border-2 border-destructive/50 bg-destructive/5 p-5 text-center">
              <h3 className="text-base font-black text-foreground">تعذّر الإرسال؟ ابعث على واتساب وسنتولى الرفع</h3>
              <p className="mt-2 text-sm text-muted-foreground">{fatalError}</p>
              <a
                href={buildFallbackWaUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-black text-white shadow-premium hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" /> ابعث على واتساب
              </a>
            </div>
          )}

          <button disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-black text-primary-foreground shadow-premium disabled:opacity-60">
            <Send className="h-4 w-4" /> {submitting ? t("submit.submitting") : t("submit.submitBtn")}
          </button>
        </form>
      </div>

      {/* Floating WhatsApp corner button (always visible on /submit) */}
      <a
        href={cornerWaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 end-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-black text-white shadow-premium hover:scale-105 transition"
      >
        <MessageCircle className="h-4 w-4" /> صعب عليك؟ ابعث على واتساب
      </a>
    </section>
  );
};

class PhasedError extends Error {
  phase: string;
  messageKey: string;
  cause?: unknown;
  meta?: Record<string, unknown>;
  constructor(phase: string, messageKey: string, cause?: unknown, meta?: Record<string, unknown>) {
    super(`[${phase}] ${extractErrorMessage(cause)}`);
    this.phase = phase;
    this.messageKey = messageKey;
    this.cause = cause;
    this.meta = meta;
  }
}

function classifyError(err: unknown): string {
  if (!err) return "Unknown";
  const e = err as { name?: string; code?: string; message?: string };
  const msg = (e.message || "").toLowerCase();
  if (e.code === "42501" || msg.includes("row-level security") || msg.includes("rls")) return "RLSViolation";
  if (e.code === "23505" || msg.includes("duplicate")) return "UniqueViolation";
  if (e.code === "23503") return "ForeignKeyViolation";
  if (e.code === "23502") return "NotNullViolation";
  if (msg.includes("heic") || msg.includes("heif")) return "HEICDecodeError";
  if (msg.includes("decode") || msg.includes("image")) return "ImageDecodeError";
  if (msg.includes("storage") || msg.includes("bucket")) return "StorageError";
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch")) return "NetworkError";
  if (e.name) return e.name;
  return "GenericError";
}

function extractErrorMessage(err: unknown): string {
  if (!err) return "Unknown error";
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "object") {
    const e = err as { message?: string; error?: string; details?: string; hint?: string; code?: string };
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(" | ") || JSON.stringify(err);
  }
  return String(err);
}

function friendlyErrorKey(err: unknown): string {
  const msg = extractErrorMessage(err).toLowerCase();
  if (msg.includes("row-level security") || msg.includes("rls")) return "submit.errors.permission";
  if (msg.includes("storage") || msg.includes("bucket") || msg.includes("upload") || msg.includes("imageuploadfailed")) return "submit.errors.imageUpload";
  if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to fetch")) return "submit.errors.network";
  if (msg.includes("duplicate")) return "submit.errors.duplicate";
  return "submit.errors.generic";
}

const inputCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
    {children}
  </label>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-4">
    <h2 className="border-b border-border pb-2 text-lg font-black">{title}</h2>
    {children}
  </div>
);

const Toggle = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm cursor-pointer">
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

export default SubmitProperty;
